/**
 * VoicemailRecording — recording-in-progress view with countdown timer.
 *
 * Shown while the user is recording a video message via Jitsi/Jibri.
 * Displays a countdown timer and stop button.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { appNavigate } from '../../../app/actions';
import { hangup } from '../../../base/connection/actions';
import { JitsiRecordingConstants } from '../../../base/lib-jitsi-meet';
import { setVideoMuted } from '../../../base/media/actions';
import { MEDIA_TYPE, VIDEO_MUTISM_AUTHORITY } from '../../../base/media/constants';
import { createDesiredLocalTracks } from '../../../base/tracks/actions.any';
import { getLocalAudioTrack, getLocalVideoTrack } from '../../../base/tracks/functions.any';
import { twilioVoiceService } from '../../../interpreter-phone/services/TwilioVoiceService';
import { getPersistentJson, setPersistentItem } from '../../../vrs-auth/storage';
import { captureRequested, dismissPrompt, recordingCancelled, recordingComplete, recordingSent, recordingStarted } from '../../actions';
import { startVideoMailCapture, usesServerRecorder } from '../../capture';
import { cancelVideoMailGreeting, cancelVoicemailRecording, getVideoMailGreetingDraft, publishVideoMailGreeting, sendVoicemailRecording, startVideoMailGreeting, startVoicemailRecording, uploadVideoMailRecording } from '../../functions';
import { canCancelBeforeTake, getRecordControlState } from '../../recordControl';
import { VIDEOMAIL_CAMERA_WAIT_MS, VIDEOMAIL_RECORDER_ON_WAIT_MS, getVideoMailCapturePhase } from '../../recordingCameraGuard';
import { useVideoMailTakeProgress } from '../../useVideoMailTakeProgress';
function getInboundHearingCall(roomName) {
    const activeCall = getPersistentJson('vrs_active_call');
    return activeCall?.requestKind === 'inbound_hearing'
        && activeCall.videoMailRelay === true
        && Boolean(roomName)
        && activeCall.roomName === roomName
        ? activeCall
        : undefined;
}
const OVERLAY_STYLE = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.9)',
    zIndex: 1800,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
};
const RECORDING_DOT_STYLE = {
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: '#e53935',
    display: 'inline-block',
    marginRight: 10,
    animation: 'pulse 1.5s infinite'
};
const TIMER_STYLE = {
    fontSize: 56,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    marginBottom: 16
};
const LABEL_STYLE = {
    fontSize: 16,
    color: '#aaa',
    marginBottom: 32,
    display: 'flex',
    alignItems: 'center'
};
// One control, shaped like a phone camera's: a ring holding a red shape that
// is a circle before a take and a rounded square during one.
const RECORD_BUTTON_STYLE = {
    width: 76,
    height: 76,
    borderRadius: '50%',
    background: 'transparent',
    border: '3px solid rgba(255,255,255,0.9)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0
};
const RECORD_INNER_CIRCLE_STYLE = {
    width: 58,
    height: 58,
    borderRadius: '50%',
    background: '#e53935',
    transition: 'width 0.2s ease, height 0.2s ease, border-radius 0.2s ease'
};
const RECORD_INNER_SQUARE_STYLE = {
    ...RECORD_INNER_CIRCLE_STYLE,
    width: 28,
    height: 28,
    borderRadius: 6
};
const CANCEL_LINK_STYLE = {
    marginTop: 24,
    background: 'transparent',
    border: 'none',
    color: '#888',
    fontSize: 14,
    cursor: 'pointer',
    textDecoration: 'underline'
};
const REVIEW_BUTTON_ROW_STYLE = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'center',
    marginTop: 24
};
const PRIMARY_BUTTON_STYLE = {
    background: '#0b7a3b',
    border: 'none',
    borderRadius: 10,
    color: '#fff',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 700,
    padding: '12px 20px'
};
const SECONDARY_BUTTON_STYLE = {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: 10,
    color: '#fff',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 600,
    padding: '12px 20px'
};
const RETRY_BUTTON_STYLE = {
    ...SECONDARY_BUTTON_STYLE,
    marginBottom: 20
};
const CAMERA_UNAVAILABLE_COPY = 'Allow camera access in your browser.';
// The callee's greeting before a message take, at its own shape.
const GREETING_VIDEO_STYLE = {
    background: '#05070d',
    borderRadius: 12,
    marginBottom: 20,
    maxHeight: '46vh',
    maxWidth: 'min(88vw, 560px)',
    width: 'auto'
};
const SKIP_BUTTON_STYLE = {
    ...PRIMARY_BUTTON_STYLE,
    minHeight: 48,
    minWidth: 140
};
/**
 * Whether the browser asks for reduced motion: the greeting then waits for
 * Play, as on the phone with Reduce Motion.
 *
 * @returns {boolean}
 */
function prefersReducedMotion() {
    try {
        return typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
    }
    catch {
        return false;
    }
}
const REVIEW_VIDEO_STYLE = {
    background: '#05070d',
    borderRadius: 12,
    marginTop: 18,
    maxHeight: '42vh',
    maxWidth: 'min(88vw, 560px)',
    // The take's own shape: a webcam is landscape, a phone browser portrait.
    width: 'auto'
};
function formatTimer(seconds) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}
const VoicemailRecording = () => {
    const dispatch = useDispatch();
    const { isCaptureFailed, isCaptureRequested, isRecording, isRecordingReadyToSend, recordingSession } = useSelector((state) => state['features/voicemail']);
    const conferenceState = useSelector((state) => state['features/base/conference']);
    const conference = conferenceState?.conference;
    const conferenceRoom = conferenceState?.room;
    const localVideoTrack = useSelector((state) => getLocalVideoTrack(state['features/base/tracks']));
    const localAudioTrack = useSelector((state) => getLocalAudioTrack(state['features/base/tracks']));
    const fileRecordingSession = useSelector((state) => state['features/recording']?.sessionDatas.find(session => session.mode === JitsiRecordingConstants.mode.FILE
        && (session.status === JitsiRecordingConstants.status.ON
            || session.status === JitsiRecordingConstants.status.PENDING)));
    const [elapsed, setElapsed] = useState(0);
    const [stopRequested, setStopRequested] = useState(false);
    const [actionPending, setActionPending] = useState(null);
    const [actionError, setActionError] = useState();
    const stopRequestOwner = useRef(null);
    const actionOwner = useMemo(() => ({ messageId: recordingSession?.messageId, roomName: conferenceRoom }), [recordingSession?.messageId, conferenceRoom]);
    const currentActionOwner = useRef(actionOwner);
    currentActionOwner.current = actionOwner;
    const pendingAction = useRef(null);
    const [confirmedAction, setConfirmedAction] = useState(null);
    const confirmedActionRef = useRef(confirmedAction);
    const mounted = useRef(true);
    const confirmed = confirmedAction?.owner === actionOwner ? confirmedAction.action : null;
    useEffect(() => {
        mounted.current = true;
        setActionPending(null);
        setActionError(undefined);
        return () => { mounted.current = false; };
    }, [actionOwner]);
    const ownsAction = useCallback(() => mounted.current && currentActionOwner.current === actionOwner, [actionOwner]);
    const [greetingPlaybackUrl, setGreetingPlaybackUrl] = useState(null);
    const [cameraAttempt, setCameraAttempt] = useState(0);
    const [cameraWaitExpired, setCameraWaitExpired] = useState(false);
    const [recorderWaitExpired, setRecorderWaitExpired] = useState(false);
    const localTake = useRef(null);
    const finishLocalTakeRef = useRef();
    const [localTakeRunning, setLocalTakeRunning] = useState(false);
    const isGreeting = recordingSession?.kind === 'greeting';
    const cameraReady = Boolean(localVideoTrack?.jitsiTrack && !localVideoTrack.muted);
    const recorderOn = fileRecordingSession?.status === JitsiRecordingConstants.status.ON;
    const capturePhase = getVideoMailCapturePhase({
        cameraReady,
        cameraWaitExpired,
        // A take recorded here starts the moment it is pressed, so there is
        // nothing to confirm and no arming gap.
        recorderConfirmed: usesServerRecorder() ? recorderOn || recorderWaitExpired : true,
        recordingSessionId: usesServerRecorder()
            ? fileRecordingSession?.id
            : localTakeRunning ? 'local' : undefined
    });
    // Only a take the server-side recorder captures has a recorder to wait
    // for and a file that arrives later; a take recorded here is uploaded
    // by finishLocalTake.
    const { saveTimedOut, saving } = useVideoMailTakeProgress({
        capturing: usesServerRecorder() && capturePhase === 'recording',
        isGreeting,
        messageId: recordingSession?.messageId,
        roomName: conferenceRoom,
        sessionId: usesServerRecorder() ? fileRecordingSession?.id : undefined,
        waitingForRecorder: usesServerRecorder() && isCaptureRequested && !fileRecordingSession?.id && !stopRequested
    });
    // The recorder announces itself once it is really encoding. If that
    // announcement never arrives, stop waiting rather than stranding a take
    // that cannot be stopped or saved.
    useEffect(() => {
        if (!fileRecordingSession?.id || recorderOn) {
            setRecorderWaitExpired(false);
            return undefined;
        }
        const timeout = setTimeout(() => setRecorderWaitExpired(true), VIDEOMAIL_RECORDER_ON_WAIT_MS);
        return () => clearTimeout(timeout);
    }, [fileRecordingSession?.id, recorderOn]);
    useEffect(() => {
        if (capturePhase !== 'recording') {
            return undefined;
        }
        const interval = setInterval(() => {
            setElapsed(prev => prev + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [capturePhase]);
    useEffect(() => {
        if (!isRecording
            || conferenceRoom !== recordingSession?.roomName
            || cameraReady
            || fileRecordingSession?.id) {
            setCameraWaitExpired(false);
            return undefined;
        }
        setCameraWaitExpired(false);
        dispatch(setVideoMuted(false, VIDEO_MUTISM_AUTHORITY.USER, true));
        dispatch(createDesiredLocalTracks(MEDIA_TYPE.VIDEO));
        const timeout = setTimeout(() => setCameraWaitExpired(true), VIDEOMAIL_CAMERA_WAIT_MS);
        return () => clearTimeout(timeout);
    }, [
        cameraAttempt,
        cameraReady,
        conferenceRoom,
        dispatch,
        fileRecordingSession?.id,
        isRecording,
        recordingSession?.messageId,
        recordingSession?.roomName
    ]);
    // Reset elapsed when recording starts
    useEffect(() => {
        if (isRecording) {
            setElapsed(0);
            setStopRequested(false);
            stopRequestOwner.current = null;
            setActionPending(null);
        }
    }, [isRecording, recordingSession?.messageId]);
    useEffect(() => {
        let mounted = true;
        if (!isRecordingReadyToSend || !isGreeting || !recordingSession?.messageId) {
            setGreetingPlaybackUrl(null);
            return undefined;
        }
        getVideoMailGreetingDraft(recordingSession.messageId).then(result => {
            if (mounted) {
                setGreetingPlaybackUrl(result.greeting?.playbackUrl || null);
            }
        }).catch(() => {
            if (mounted) {
                setGreetingPlaybackUrl(null);
            }
        });
        return () => {
            mounted = false;
        };
    }, [isGreeting, isRecordingReadyToSend, recordingSession?.messageId]);
    const remaining = Math.max(0, (recordingSession?.maxDurationSeconds || 180) - elapsed);
    // Server-recorder takes only: a second press, or a press after the first
    // stop, must not ask the recorder to stop the same session again.
    const handleStop = useCallback(() => {
        const sessionId = fileRecordingSession?.id;
        if (!sessionId || stopRequestOwner.current === sessionId) {
            return;
        }
        if (typeof conference?.stopRecording !== 'function') {
            setActionError('Recording could not be stopped. Try again.');
            return;
        }
        stopRequestOwner.current = sessionId;
        setStopRequested(true);
        try {
            void Promise.resolve(conference.stopRecording(sessionId)).catch(() => {
                if (stopRequestOwner.current === sessionId) {
                    setActionError('Stop not confirmed.');
                }
            });
        }
        catch {
            setActionError('Stop not confirmed.');
        }
    }, [conference, fileRecordingSession?.id]);
    const recordControl = getRecordControlState({
        capturePhase,
        captureRequested: isCaptureRequested,
        saving,
        stopRequested
    });
    const cancelShown = saveTimedOut
        || canCancelBeforeTake({ capturePhase, captureRequested: isCaptureRequested, saving, stopRequested });
    const stageError = actionError
        || (saveTimedOut ? 'Recording not saved.' : isCaptureFailed ? 'Recording did not start. Try again.' : undefined);
    // A take recorded in this browser: the camera is already open, so pressing
    // record starts writing immediately instead of waiting on a recorder that
    // has to launch elsewhere and join.
    const startLocalTake = useCallback(() => {
        const mediaTracks = [localAudioTrack?.jitsiTrack, localVideoTrack?.jitsiTrack]
            .map(track => track?.getTrack?.())
            .filter(Boolean);
        if (!mediaTracks.length) {
            setActionError('No camera or microphone is available to record.');
            return;
        }
        try {
            localTake.current = startVideoMailCapture({
                stream: new MediaStream(mediaTracks),
                maxDurationSeconds: recordingSession?.maxDurationSeconds,
                // The limit ends the take the same way the button does, so it
                // is uploaded rather than dropped.
                onAutoStop: () => finishLocalTakeRef.current?.()
            });
            setActionError(undefined);
            setLocalTakeRunning(true);
        }
        catch (error) {
            setActionError(error instanceof Error ? error.message : 'Recording could not start.');
        }
    }, [localAudioTrack, localVideoTrack, recordingSession?.maxDurationSeconds]);
    const finishLocalTake = useCallback(async () => {
        const take = localTake.current;
        if (!take || !recordingSession?.messageId) {
            return;
        }
        // Claim the take before waiting on it, so the duration limit and a
        // press landing together finish and upload it once.
        localTake.current = null;
        setStopRequested(true);
        try {
            const finished = await take.stop();
            await uploadVideoMailRecording({
                blob: finished.blob,
                contentType: finished.contentType,
                durationSeconds: finished.durationSeconds,
                ...(isGreeting
                    ? { greetingId: recordingSession.messageId }
                    : { messageId: recordingSession.messageId })
            });
            if (!ownsAction()) {
                return;
            }
            setLocalTakeRunning(false);
            dispatch(recordingComplete(recordingSession.messageId, finished.durationSeconds, isGreeting ? 'greeting' : 'message'));
        }
        catch (error) {
            localTake.current = null;
            if (!ownsAction()) {
                return;
            }
            setLocalTakeRunning(false);
            setStopRequested(false);
            setActionError(error instanceof Error ? error.message : 'The recording could not be saved. Record again.');
        }
    }, [dispatch, isGreeting, ownsAction, recordingSession?.messageId]);
    finishLocalTakeRef.current = () => void finishLocalTake();
    // Leaving the recorder must not leave a camera writing into a take nobody
    // will collect.
    useEffect(() => () => {
        const take = localTake.current;
        localTake.current = null;
        take?.stop().catch(() => { });
    }, []);
    const handleRecordPress = useCallback(() => {
        if (recordControl.action === 'start') {
            if (usesServerRecorder()) {
                dispatch(captureRequested());
            }
            else {
                startLocalTake();
            }
        }
        else if (recordControl.action === 'stop') {
            if (usesServerRecorder()) {
                handleStop();
            }
            else {
                void finishLocalTake();
            }
        }
    }, [dispatch, finishLocalTake, handleStop, recordControl.action, startLocalTake]);
    // A message take opens with the callee's greeting, as on the phone
    // (VideoMailRecorderScreen.tsx; Robert, 2026-10-29: "skip and go straight
    // to videomail or cancel leaving one"). It plays muted (Robert,
    // 2026-11-01: "without sound preferable"). With reduced motion it waits
    // for Play. Skip and Cancel show the whole time. A clip that does not
    // load is skipped. Then the first take starts by itself, also when there
    // is no greeting; Record again waits for the press.
    const greetingUrl = isGreeting ? undefined : recordingSession?.calleeGreeting?.playbackUrl;
    const [greetingOver, setGreetingOver] = useState(false);
    const [reducedMotion] = useState(prefersReducedMotion);
    const autoStarted = useRef(false);
    const greetingPlaying = Boolean(greetingUrl) && !greetingOver && capturePhase !== 'recording'
        && !isCaptureRequested && !saving && !stopRequested;
    const endGreeting = useCallback(() => setGreetingOver(true), []);
    const greetingVideo = useRef(null);
    // React does not always write the muted attribute, and a browser starts
    // a video by itself only when it is muted: set it on the element first.
    useEffect(() => {
        const video = greetingVideo.current;
        if (!greetingPlaying || !video) {
            return;
        }
        video.defaultMuted = true;
        video.muted = true;
        if (!reducedMotion) {
            Promise.resolve(video.play()).catch(() => undefined);
        }
    }, [greetingPlaying, greetingUrl, reducedMotion]);
    useEffect(() => {
        if (isGreeting || autoStarted.current || greetingPlaying || isRecordingReadyToSend
            || recordControl.action !== 'start' || !recordControl.enabled) {
            return;
        }
        autoStarted.current = true;
        handleRecordPress();
    }, [greetingPlaying, handleRecordPress, isGreeting, isRecordingReadyToSend, recordControl.action, recordControl.enabled]);
    const finishGreetingLocally = useCallback(() => {
        dispatch(recordingCancelled());
        dispatch(dismissPrompt());
        dispatch(hangup(false));
    }, [dispatch]);
    const handleCancel = useCallback(async () => {
        if (pendingAction.current === actionOwner || confirmedActionRef.current?.owner === actionOwner
            && confirmedActionRef.current.action === 'send')
            return;
        pendingAction.current = actionOwner;
        setActionPending('discard');
        setActionError(undefined);
        try {
            if (isGreeting && recordingSession?.messageId) {
                const cancelRequest = cancelVideoMailGreeting(recordingSession.messageId);
                finishGreetingLocally();
                cancelRequest.catch((error) => {
                    console.warn('[VideoMail] Failed to cancel greeting after leaving the recorder.', error);
                });
                return;
            }
            if (recordingSession?.messageId && confirmedActionRef.current?.owner !== actionOwner) {
                await cancelVoicemailRecording(recordingSession.messageId);
                if (!ownsAction())
                    return;
                const receipt = { owner: actionOwner, action: 'discard' };
                confirmedActionRef.current = receipt;
                setConfirmedAction(receipt);
            }
            if (!ownsAction())
                return;
            if (getInboundHearingCall(conferenceRoom))
                await twilioVoiceService.hangupCall();
            if (!ownsAction())
                return;
            dispatch(recordingCancelled());
            dispatch(dismissPrompt());
            dispatch(hangup(false));
        }
        catch (error) {
            if (ownsAction())
                setActionError(error instanceof Error ? error.message : 'Discard or phone hangup could not be confirmed. Try again.');
        }
        finally {
            if (pendingAction.current === actionOwner)
                pendingAction.current = null;
            if (ownsAction())
                setActionPending(null);
        }
    }, [actionOwner, conferenceRoom, dispatch, finishGreetingLocally, isGreeting, ownsAction, recordingSession]);
    const handleRetryCamera = useCallback(() => {
        setCameraAttempt(attempt => attempt + 1);
    }, []);
    const handleRecordAgain = useCallback(async () => {
        if (pendingAction.current === actionOwner || confirmedActionRef.current?.owner === actionOwner)
            return;
        if (!recordingSession?.messageId || (!isGreeting && !recordingSession.calleeId && !recordingSession.calleePhone)) {
            return;
        }
        setActionPending('recordAgain');
        let activeCallBeforeNavigation;
        let preserveArmed = false;
        try {
            if (isGreeting) {
                await cancelVideoMailGreeting(recordingSession.messageId);
            }
            else {
                await cancelVoicemailRecording(recordingSession.messageId);
            }
            const result = isGreeting
                ? await startVideoMailGreeting()
                : await startVoicemailRecording({
                    calleeId: recordingSession.calleeId,
                    calleePhone: recordingSession.calleePhone
                });
            dispatch(recordingStarted({
                kind: isGreeting ? 'greeting' : 'message',
                calleeId: recordingSession.calleeId,
                calleeName: recordingSession.calleeName,
                calleePhone: recordingSession.calleePhone,
                messageId: result.messageId,
                roomName: result.roomName,
                maxDurationSeconds: result.maxDurationSeconds
            }));
            activeCallBeforeNavigation = getInboundHearingCall(conferenceRoom);
            if (activeCallBeforeNavigation) {
                setPersistentItem('vrs_active_call', JSON.stringify({
                    ...activeCallBeforeNavigation,
                    roomName: result.roomName,
                    videoMailRelay: true
                }));
                twilioVoiceService.preserveCurrentCallForNavigation();
                preserveArmed = true;
            }
            try {
                const navigationSucceeded = await Promise.resolve(dispatch(appNavigate(result.roomName, {
                    hidePrejoin: true,
                    startWithAudioMuted: false,
                    startWithVideoMuted: false
                })));
                if (navigationSucceeded === false) {
                    throw new Error('Could not join the VideoMail room.');
                }
            }
            catch (error) {
                if (activeCallBeforeNavigation) {
                    setPersistentItem('vrs_active_call', JSON.stringify(activeCallBeforeNavigation));
                }
                throw error;
            }
        }
        catch (error) {
            if (preserveArmed) {
                twilioVoiceService.cancelPreserveCurrentCallForNavigation();
            }
            dispatch({
                type: 'VOICEMAIL_ERROR',
                error: error instanceof Error ? error.message : 'Failed to restart voicemail recording'
            });
        }
        finally {
            setActionPending(null);
        }
    }, [actionOwner, conferenceRoom, dispatch, isGreeting, recordingSession]);
    const handleSend = useCallback(async () => {
        if (!recordingSession?.messageId || pendingAction.current === actionOwner
            || confirmedActionRef.current?.owner === actionOwner && confirmedActionRef.current.action === 'discard')
            return;
        pendingAction.current = actionOwner;
        setActionPending('send');
        setActionError(undefined);
        try {
            if (confirmedActionRef.current?.owner !== actionOwner) {
                if (isGreeting)
                    await publishVideoMailGreeting(recordingSession.messageId);
                else
                    await sendVoicemailRecording(recordingSession.messageId);
                if (!ownsAction())
                    return;
                const receipt = { owner: actionOwner, action: 'send' };
                confirmedActionRef.current = receipt;
                setConfirmedAction(receipt);
            }
            if (!ownsAction())
                return;
            if (getInboundHearingCall(conferenceRoom))
                await twilioVoiceService.hangupCall();
            if (!ownsAction())
                return;
            dispatch(recordingSent(recordingSession.messageId));
            dispatch(dismissPrompt());
            dispatch(hangup(false));
        }
        catch (error) {
            if (ownsAction())
                setActionError(error instanceof Error ? error.message : 'Send or phone hangup could not be confirmed. Try again.');
        }
        finally {
            if (pendingAction.current === actionOwner)
                pendingAction.current = null;
            if (ownsAction())
                setActionPending(null);
        }
    }, [actionOwner, conferenceRoom, dispatch, isGreeting, ownsAction, recordingSession]);
    if ((!isRecording && !isRecordingReadyToSend) || !recordingSession) {
        return null;
    }
    if (isRecordingReadyToSend) {
        return (React.createElement("div", { style: OVERLAY_STYLE },
            React.createElement("div", { style: LABEL_STYLE }, confirmed === 'send' ? 'VideoMail sent' : confirmed === 'discard' ? 'VideoMail discarded'
                : isGreeting ? 'VideoMail greeting ready' : 'VideoMail draft ready'),
            React.createElement("div", { style: { fontSize: 30, fontWeight: 700, marginBottom: 12 } }, isGreeting ? 'Review your greeting' : (recordingSession.calleeName ? `For ${recordingSession.calleeName}` : 'Ready to send')),
            isGreeting && greetingPlaybackUrl ? (React.createElement("video", { controls: true, playsInline: true, src: greetingPlaybackUrl, style: REVIEW_VIDEO_STYLE })) : null,
            actionError ? React.createElement("div", { role: "alert", style: { color: '#fff', maxWidth: 440, marginTop: 16 } }, actionError) : null,
            React.createElement("div", { style: REVIEW_BUTTON_ROW_STYLE },
                React.createElement("button", { disabled: Boolean(actionPending), onClick: confirmed === 'discard' ? handleCancel : handleSend, style: {
                        ...PRIMARY_BUTTON_STYLE,
                        opacity: actionPending ? 0.65 : 1
                    }, type: 'button' }, confirmed ? actionPending ? 'Ending…' : 'Retry ending call'
                    : actionPending === 'send' ? (isGreeting ? 'Publishing...' : 'Sending...') : (isGreeting ? 'Use This Greeting' : 'Send VideoMail')),
                React.createElement("button", { disabled: Boolean(actionPending) || Boolean(confirmed) || (!isGreeting && !recordingSession.calleeId && !recordingSession.calleePhone), onClick: handleRecordAgain, style: {
                        ...SECONDARY_BUTTON_STYLE,
                        opacity: actionPending ? 0.65 : 1
                    }, type: 'button' }, actionPending === 'recordAgain' ? 'Starting...' : 'Record Again'),
                React.createElement("button", { disabled: Boolean(actionPending) || Boolean(confirmed), onClick: handleCancel, style: {
                        ...SECONDARY_BUTTON_STYLE,
                        opacity: actionPending ? 0.65 : 1
                    }, type: 'button' }, actionPending === 'discard' ? 'Discarding...' : 'Discard And End'))));
    }
    return (React.createElement("div", { style: OVERLAY_STYLE },
        stageError ? React.createElement("div", { role: "alert", style: { color: '#fff', maxWidth: 440, marginBottom: 16 } }, stageError) : null,
        React.createElement("div", { style: LABEL_STYLE },
            capturePhase === 'recording' && !saving ? React.createElement("span", { style: RECORDING_DOT_STYLE }) : null,
            saving
                ? 'Finishing recording...'
                : capturePhase === 'recording'
                    ? isGreeting ? 'Recording VideoMail greeting...' : 'Recording video message...'
                    : isCaptureRequested || capturePhase === 'arming'
                        ? 'Starting recorder...'
                        : capturePhase === 'starting'
                            ? isGreeting ? 'Ready to record your greeting' : 'Ready to record'
                            : capturePhase === 'camera-unavailable'
                                ? 'Camera unavailable'
                                : 'Waiting for camera...'),
        React.createElement("div", { style: TIMER_STYLE }, capturePhase === 'recording' ? formatTimer(elapsed) : '--:--'),
        React.createElement("div", { style: { fontSize: 14, color: '#666', marginBottom: 32 } }, stopRequested || saving
            ? 'Finalizing recording...'
            : capturePhase === 'recording'
                ? `${formatTimer(remaining)} remaining`
                : capturePhase === 'camera-unavailable'
                    ? CAMERA_UNAVAILABLE_COPY
                    : isCaptureRequested || capturePhase === 'arming'
                        ? 'Wait for the timer.'
                        : ''),
        capturePhase === 'camera-unavailable' ? (React.createElement("button", { onClick: handleRetryCamera, style: RETRY_BUTTON_STYLE, type: 'button' }, "Retry Camera")) : null,
        greetingPlaying ? (React.createElement(React.Fragment, null,
            React.createElement("video", { "aria-label": `${recordingSession?.calleeName || 'Their'} VideoMail greeting`, controls: reducedMotion, muted: true, onEnded: endGreeting, onError: endGreeting, playsInline: true, poster: recordingSession?.calleeGreeting?.thumbnailUrl || undefined, ref: greetingVideo, src: greetingUrl, style: GREETING_VIDEO_STYLE }),
            React.createElement("button", { "aria-label": 'Skip greeting and record now', onClick: endGreeting, style: SKIP_BUTTON_STYLE, type: 'button' }, "Skip"))) : (React.createElement("button", { "aria-label": recordControl.label, disabled: !recordControl.enabled, onClick: handleRecordPress, style: {
                ...RECORD_BUTTON_STYLE,
                cursor: recordControl.enabled ? 'pointer' : recordControl.busy ? 'wait' : 'not-allowed',
                opacity: recordControl.enabled ? 1 : 0.55
            }, title: recordControl.label, type: 'button' },
            React.createElement("div", { style: recordControl.shape === 'square' ? RECORD_INNER_SQUARE_STYLE : RECORD_INNER_CIRCLE_STYLE }))),
        cancelShown ? (React.createElement("button", { onClick: handleCancel, style: CANCEL_LINK_STYLE, type: 'button' }, "Cancel And End")) : null));
};
export default VoicemailRecording;
